/**
 * Menu Component
 *
 * Handles parsing and formatting of menu structures.
 * Similar to component structure: contains input interface, parser, and formatter.
 */

import { v4 as uuidv4 } from 'uuid'

import { IMenu, IMenuItem } from '@makyoapp/makyo-sdk/models/AppBuilder.js'

import { parseToVariableText, extractTextFromVariableText } from '../components/parser-utils'

/**
 * Simplified AI Menu Item Input
 */
export interface AiMenuItemInput {
  title: string
  icon?: string
  badgeSize?: 's' | 'm' | 'l' | ''
  badgeMetadata?: Record<string, any>
  actions?: any[] // Actions will be parsed later (deferred to action parser)
  visibility?: {
    conditions?: any[]
    isAny?: boolean
  }
}

/**
 * Simplified AI Menu Input
 */
export interface AiMenuInput {
  type: 'menu'
  title: string
  items: AiMenuItemInput[]
  visibility?: {
    conditions?: any[]
    isAny?: boolean
  }
}

/**
 * Parse AI menu item input to full menu item structure
 */
function parseMenuItem(input: AiMenuItemInput): IMenuItem {
  const itemId = uuidv4()

  // Convert VariableTextValue to JSON string if needed
  // For simplicity, we'll accept plain strings and convert them
  const titleValue = parseToVariableText(input.title)
  const titleString = typeof titleValue === 'string' ? titleValue : JSON.stringify(titleValue)

  return {
    _id: itemId,
    _Title: titleString,
    _Icon: input.icon
      ? {
          flag: 8,
          id: '',
          value: input.icon,
        }
      : {
          flag: 8,
          id: '',
          value: '',
        },
    _BadgeSize: input.badgeSize || '',
    _BadgeMetadata: input.badgeMetadata || {},
    _Actions: input.actions
      ? {
          'action-sequence': {
            _Cond: '',
            _Actions: input.actions,
          },
        }
      : {
          'action-sequence': {
            _Cond: '',
            _Actions: [],
          },
        },
    _Visibility: input.visibility || {},
  }
}

/**
 * Format menu item from IMenuItem to AiMenuItemInput
 */
function formatMenuItem(item: IMenuItem): AiMenuItemInput {
  // Parse title from JSON string or plain string
  let title = ''
  try {
    const titleParsed = JSON.parse(item._Title)
    title = extractTextFromVariableText(titleParsed)
  } catch {
    title = item._Title
  }

  // Format icon
  const icon = item._Icon?.value
  const iconFormatted = icon ? (icon.startsWith('icon:') ? icon : `icon:${icon}`) : undefined

  const result: AiMenuItemInput = {
    title,
  }

  // Only include optional properties if they have values
  if (iconFormatted) {
    result.icon = iconFormatted
  }
  if (item._BadgeSize) {
    result.badgeSize = item._BadgeSize as 's' | 'm' | 'l' | ''
  }
  if (item._BadgeMetadata && Object.keys(item._BadgeMetadata).length > 0) {
    result.badgeMetadata = item._BadgeMetadata
  }
  if (item._Actions && item._Actions['action-sequence'] && item._Actions['action-sequence']._Actions.length > 0) {
    result.actions = item._Actions['action-sequence']._Actions
  }
  if (item._Visibility && (item._Visibility.conditions?.length > 0 || item._Visibility.isAny)) {
    result.visibility = item._Visibility
  }

  return result
}

/**
 * Parse AI menu input to full menu structure
 */
export function parseAiMenu(input: AiMenuInput): IMenu {
  const menuId = uuidv4()

  // Parse menu items
  const menuItems: IMenuItem[] = input.items.map((item) => parseMenuItem(item))

  // Convert VariableTextValue to JSON string if needed
  const titleValue = parseToVariableText(input.title)
  const titleString = typeof titleValue === 'string' ? titleValue : JSON.stringify(titleValue)

  // Build menu structure
  const fullMenu: IMenu = {
    _id: menuId,
    _Title: titleString,
    _MenuItem: menuItems,
    _Visibility: input.visibility || {},
  }

  return fullMenu
}

/**
 * Format IMenu to AiMenuInput
 */
export function formatAiMenu(menu: IMenu): AiMenuInput {
  // Parse title from JSON string or plain string
  let title = ''
  try {
    const titleParsed = JSON.parse(menu._Title)
    title = extractTextFromVariableText(titleParsed)
  } catch {
    title = menu._Title
  }

  // Format menu items
  const items = (menu._MenuItem || []).map((item) => formatMenuItem(item))

  const result: AiMenuInput = {
    type: 'menu',
    title,
    items,
  }

  // Only include optional properties if they have values
  if (menu._Visibility && (menu._Visibility.conditions?.length > 0 || menu._Visibility.isAny)) {
    result.visibility = menu._Visibility
  }

  return result
}

